'use client';

import { ReactNode, useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { Position, PRODUCT_LEGAL_MENTIONS_DTS_MENU_ID } from '@headless-workspace/config';
import { unwrapResultSuccess } from '@headless-workspace/core-domain';
import { SideMenuModal, useModalActions } from '@headless-workspace/core-ui';
import { GlowModal } from '@headless-workspace/glow-ds';
import {
    InnerHTML,
    ProductDetailInfoModal,
    ProductType,
    useDTSLegalMentionsAssets,
    useProductDetailContext,
    useStoreInfoCardUtils,
} from '../../../../../client';

export const useStoreSelectionHelpers = () => {
    const { addAndOpen: addAndOpenMenu } = useModalActions();
    const t = useTranslations('Modal.stores.card.menuDTS');

    const { productDetails } = useProductDetailContext();
    // The case where the sku is not defined is impossible at this level, as this hook is only called by components that are not rendered for products of type PRODUCT_SET.
    const sku = productDetails.type !== ProductType.PRODUCT_SET ? productDetails.sku : undefined;

    const { mapStoreInfoToUIStoreInfoCardStore } = useStoreInfoCardUtils(sku);

    const { result } = useDTSLegalMentionsAssets();
    const legalMentionsAssetsDTS = unwrapResultSuccess(result);

    const legalMentionsDTSModal = useMemo(
        () => (
            <ProductDetailInfoModal
                title={t('label.title')}
                closeButtonLabel={t('action.close')}
                productModalId={PRODUCT_LEGAL_MENTIONS_DTS_MENU_ID}
                renderInformation={
                    <GlowModal.Content>
                        <InnerHTML rawHtml={legalMentionsAssetsDTS?.content.html} className={'text-text-primary'} />
                    </GlowModal.Content>
                }
                hasBackCta={true}
                isSidePanelDeliveryZone={true}
            />
        ),
        [legalMentionsAssetsDTS?.content.html, t],
    );

    const onOpenMenu = useCallback(
        (content: ReactNode) => {
            const menuClassName = 'flex flex-col text-text-brand overflow-hidden';
            addAndOpenMenu(
                PRODUCT_LEGAL_MENTIONS_DTS_MENU_ID,
                SideMenuModal({ side: Position.Right, map: { default: content }, className: menuClassName }),
            );
        },
        [addAndOpenMenu],
    );

    return {
        mapStoreInfoToUIStoreInfoCardStore,
        onOpenMenu,
        legalMentionsDTSModal,
    };
};
